import React from "react";
import Image from "next/image";
import { render } from "storyblok-rich-text-react-renderer";
import { markdownToRichtext } from "storyblok-markdown-richtext";
import LinkWrapper from "../../../components/Nestable/LinkWrapper/LinkWrapper";
import { isStoryblokSlug } from "../../../lib/routing/isStoryblokSlug";
import { resolveSlug } from "../../../lib/utils/resolveSlug";
import cn from "classnames";
import s from "./RichText.module.scss";

import type {
  RichTextContent,
  RichTextLinkAttributes,
} from "../../../lib/storyblok/types/fieldtypes/richText";

interface Props {
  content: RichTextContent;
  className?: string;
  inverted?: boolean;
  alignment?: "alignLeft" | "alignCenter" | "alignRight";
}

interface StoryblokProps extends Record<string, unknown> {
  _uid: string;
}

type MarkResolverFunction = (
  children: React.ReactNode,
  props: RichTextLinkAttributes
) => React.ReactElement | null;

interface MarkResolvers {
  link: MarkResolverFunction;
  bold?: (children: React.ReactNode) => React.ReactElement;
  italic?: (children: React.ReactNode) => React.ReactElement;
  strike?: (children: React.ReactNode) => React.ReactElement;
  underline?: (children: React.ReactNode) => React.ReactElement;
  superscript?: (children: React.ReactNode) => React.ReactElement;
  subscript?: (children: React.ReactNode) => React.ReactElement;
  anchor?: (
    children: React.ReactNode,
    props: { id: string }
  ) => React.ReactElement;
}

interface TableAttributes {
  colspan?: number;
  rowspan?: number;
  colwidth?: number[] | null;
  backgroundColor?: string | null;
}

const RichText: React.FC<Props> = ({
  content,
  className,
  alignment,
  inverted,
}) => {
  if (!content) return null;

  const defaultBlokResolver = (
    name: string,
    props: StoryblokProps,
    children?: React.ReactNode
  ) => {
    switch (name) {
      case "heading": {
        const level = props.level as number;
        if (!level) return null;

        const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
        return (
          <HeadingTag
            id={props.id as string}
            className={cn(s.heading, props.class as string, `heading-${level}`)}
          >
            {children}
          </HeadingTag>
        );
      }
      case "image": {
        const src = props.src as string;
        if (!src) return null;

        return (
          <figure className={s.imageWrapper}>
            <div className={s.imageContainer}>
              <Image
                src={src}
                alt={(props.alt as string) || ""}
                title={props.title as string}
                width={(props.width as number) || 800}
                height={(props.height as number) || 600}
                className={s.image}
                loading={(props.loading as "lazy" | "eager") || "lazy"}
              />
            </div>
          </figure>
        );
      }
      case "textStyle": {
        const styles: React.CSSProperties = {
          color: props.color as string,
          backgroundColor: props.backgroundColor as string,
          textAlign: props.textAlign as "left" | "center" | "right" | "justify",
          fontFamily: props.fontFamily as string,
          fontSize: props.fontSize as string,
          fontWeight: props.fontWeight as string | number,
          lineHeight: props.lineHeight as string | number,
          letterSpacing: props.letterSpacing as string,
          textDecoration: props.textDecoration as string,
          textTransform: props.textTransform as
            | "none"
            | "capitalize"
            | "uppercase"
            | "lowercase",
        };

        return <span style={styles}>{children}</span>;
      }
      default:
        return null;
    }
  };

  const markResolvers: MarkResolvers = {
    link: (children: React.ReactNode, props: RichTextLinkAttributes) => {
      const href = props.href || props.cached_url;
      const anchor = props.anchor;
      if (!href) return null;

      if (props.linktype === "email") {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { target, ...restProps } = props;
        return React.createElement(LinkWrapper, {
          ...restProps,
          href: `mailto:${href}`,
          text: children,
        });
      }

      const slug = href.startsWith("/") ? href.slice(1) : href;
      const isSbSlug = isStoryblokSlug(slug);
      const final = isSbSlug ? resolveSlug(slug) : href;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { target, ...restProps } = props;

      return React.createElement(LinkWrapper, {
        ...restProps,
        href: final + (anchor ? `#${anchor}` : ""),
        text: children,
      });
    },
    bold: (children) => <strong className={s.bold}>{children}</strong>,
    italic: (children) => <em className={s.italic}>{children}</em>,
    strike: (children) => <s className={s.strike}>{children}</s>,
    underline: (children) => <u className={s.underline}>{children}</u>,
    superscript: (children) => <sup className={s.superscript}>{children}</sup>,
    subscript: (children) => <sub className={s.subscript}>{children}</sub>,
    anchor: (children, props) => (
      <span id={props.id} className={s.anchor}>
        {children}
      </span>
    ),
  };

  const nodeResolvers = {
    heading: (children: React.ReactNode, props: { level: number }) => {
      const level = props.level;
      const HeadingTag = `h${level}` as keyof JSX.IntrinsicElements;
      return (
        <HeadingTag className={cn(s.heading, `heading-${level}`)}>
          {children}
        </HeadingTag>
      );
    },
    heading_1: (children: React.ReactNode) => (
      <h1 className={cn(s.heading, "heading-1")}>{children}</h1>
    ),
    heading_2: (children: React.ReactNode) => (
      <h2 className={cn(s.heading, "heading-2")}>{children}</h2>
    ),
    heading_3: (children: React.ReactNode) => (
      <h3 className={cn(s.heading, "heading-3")}>{children}</h3>
    ),
    heading_4: (children: React.ReactNode) => (
      <h4 className={cn(s.heading, "heading-4")}>{children}</h4>
    ),
    heading_5: (children: React.ReactNode) => (
      <h5 className={cn(s.heading, "heading-5")}>{children}</h5>
    ),
    heading_6: (children: React.ReactNode) => (
      <h6 className={cn(s.heading, "heading-6")}>{children}</h6>
    ),
    paragraph: (children: React.ReactNode) => (
      <p className={s.paragraph}>{children}</p>
    ),
    bullet_list: (children: React.ReactNode) => (
      <ul className={s.bulletList}>{children}</ul>
    ),
    ordered_list: (children: React.ReactNode) => (
      <ol className={s.orderedList}>{children}</ol>
    ),
    list_item: (children: React.ReactNode) => (
      <li className={s.listItem}>{children}</li>
    ),
    blockquote: (children: React.ReactNode) => (
      <blockquote className={s.blockquote}>{children}</blockquote>
    ),
    horizontal_rule: () => <hr className={s.hr} />,
    hard_break: () => <br />,

    table: (children: React.ReactNode) => (
      <div className={s.tableWrapper}>
        <table className={s.table}>{children}</table>
      </div>
    ),
    tableRow: (children: React.ReactNode) => (
      <tr className={s.tableRow}>{children}</tr>
    ),
    tableHeader: (children: React.ReactNode, attrs?: TableAttributes) => {
      const style: React.CSSProperties = {};

      if (attrs?.backgroundColor) {
        style.backgroundColor = attrs.backgroundColor;
      }

      return (
        <th
          className={s.tableHeader}
          style={style}
          colSpan={attrs?.colspan || 1}
          rowSpan={attrs?.rowspan || 1}
        >
          {children}
        </th>
      );
    },
    tableCell: (children: React.ReactNode, attrs?: TableAttributes) => {
      const style: React.CSSProperties = {};

      if (attrs?.backgroundColor) {
        style.backgroundColor = attrs.backgroundColor;
      }

      return (
        <td
          className={s.tableCell}
          style={style}
          colSpan={attrs?.colspan || 1}
          rowSpan={attrs?.rowspan || 1}
        >
          {children}
        </td>
      );
    },
  };

  const renderContent = () => {
    if (typeof content === "string") {
      const markdownConverted = markdownToRichtext(content);
      return render(markdownConverted, {
        defaultBlokResolver,
        nodeResolvers,
        markResolvers,
      });
    }

    return render(content, {
      defaultBlokResolver,
      nodeResolvers,
      markResolvers,
    });
  };

  return (
    <div
      className={cn(s.richText, className, s[alignment || "alignCenter"], {
        [s.inverted]: inverted,
      })}
    >
      {renderContent()}
    </div>
  );
};

export default RichText;
